/* BOHDY landing — living 3D hero. three.js (ESM).
 *
 * Renders the interior photo onto a full-bleed plane and gives it life:
 *   - slow continuous drift (auto-pan + gentle breathing zoom)
 *   - AI depth-map parallax (near objects move more than far -> reads as 3D)
 *   - pointer / device-tilt parallax pan
 *   - touch swipe-to-pan (cover mode) to explore the full room
 *   - soft vignette + faint film grain
 *
 * Tappable hotspots: a pin dispatches `bohdy:flyrequest`; we dolly the camera
 * into the pin's focal point, crossfade to a destination photo (with its own
 * depth map), then dispatch `bohdy:flyin` so the section can open its info
 * panel. `bohdy:flyout` reverses it.
 *
 * Progressive enhancement only. The section renders a static <img> + fully
 * styled glass UI with zero JS, and the panel still opens (without the fly)
 * when this bails: opted out, reduced motion, no WebGL, or load failure.
 *
 * three.js and GSAP are self-hosted theme assets, loaded lazily from the
 * section's data-three-src / data-gsap-src attributes — and only after the
 * cheap bail checks pass, so visitors who never get the 3D treatment never
 * download the libraries either. GSAP stays optional: every animation path
 * has a window.gsap fallback.
 *
 * Re-binds on Shopify theme editor section load/unload.
 */

const SECTION_SELECTOR = '.bohdy';

const INTENSITY = {
  subtle:   { pan: 0.012, drift: 0.010, depth: 0.45, tilt: 4 },
  standard: { pan: 0.020, drift: 0.016, depth: 0.65, tilt: 7 },
  strong:   { pan: 0.030, drift: 0.022, depth: 0.90, tilt: 11 },
};

const IDLE_ZOOM = 1.06; // base zoom: gives headroom for depth-parallax shifts
const FLY_ZOOM = 1.85;  // how far we dolly into a hotspot

const scenes = new WeakMap();

function prefersReducedMotion() {
  return window.matchMedia &&
         window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function webglAvailable() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

function getIntensity(root) {
  const key = (root.getAttribute('data-motion-intensity') || 'standard').toLowerCase();
  return INTENSITY[key] || INTENSITY.standard;
}

// --- lazy library loading (shared across sections, fetched at most once) ---

let libsPromise = null;

// GSAP is a UMD build that registers window.gsap; inject it as a classic
// script rather than import() so the global registration is guaranteed.
function loadGsap(src) {
  return new Promise(function (resolve) {
    if (!src || window.gsap) { resolve(); return; }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = resolve; // optional: all gsap paths have fallbacks
    document.head.appendChild(s);
  });
}

function loadLibs(root) {
  if (!libsPromise) {
    const threeSrc = root.getAttribute('data-three-src');
    if (!threeSrc) return Promise.reject(new Error('bohdy-hero3d: missing data-three-src'));
    libsPromise = Promise.all([
      import(threeSrc),
      loadGsap(root.getAttribute('data-gsap-src')),
    ]).then(function (results) {
      return results[0];
    });
  }
  return libsPromise;
}

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform sampler2D uTex;    // hero color
  uniform sampler2D uDepth;  // hero depth (near = white)
  uniform sampler2D uTex2;   // destination color
  uniform sampler2D uDepth2; // destination depth
  uniform float uMix;        // 0 = hero, 1 = destination (crossfade)
  uniform vec2  uFocus1;     // hero dolly focal point (screen uv)
  uniform float uZoom1;      // hero zoom (>1)
  uniform vec2  uFocus2;     // destination focal point
  uniform float uZoom2;      // destination zoom
  uniform vec2  uPan;        // uniform pan (swipe) — shifts all depths
  uniform vec2  uOffset;     // parallax pan (drift + pointer) — depth-modulated
  uniform float uDepthStrength;
  uniform float uImageAspect;  // hero texture w/h
  uniform float uImageAspect2; // destination texture w/h
  uniform float uViewAspect;
  uniform float uTime;
  uniform float uGrain;
  uniform float uFit;        // 0 = cover (fill), 1 = contain (fit whole)
  uniform vec3  uPaper;      // letterbox colour for contain mode

  // object-fit uv. fit<0.5 => cover (crop), else => contain (letterbox).
  vec2 fitUv(vec2 uv, float imgA, float viewA, float fit) {
    vec2 c = vec2(0.5);
    vec2 p = uv - c;
    if (fit < 0.5) {
      if (viewA > imgA) { p.y *= imgA / viewA; } else { p.x *= viewA / imgA; }
    } else {
      if (viewA > imgA) { p.x *= viewA / imgA; } else { p.y *= imgA / viewA; }
    }
    return p + c;
  }

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  // Sample one photo with dolly (zoom around focus), uniform pan and
  // depth-modulated parallax. Returns the page colour outside the image
  // bounds when letterboxing (contain mode).
  vec4 sampleScene(sampler2D tex, sampler2D dmap, vec2 focus, float zoom, float imgA, vec2 pan) {
    vec2 uv = (vUv - focus) / zoom + focus;
    uv += pan;
    vec2 duv = clamp(fitUv(uv, imgA, uViewAspect, uFit), 0.0, 1.0);
    float d = texture2D(dmap, duv).r;
    uv += uOffset * d * uDepthStrength;
    uv = fitUv(uv, imgA, uViewAspect, uFit);
    if (uFit > 0.5 && (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0)) {
      return vec4(uPaper, 1.0);
    }
    return texture2D(tex, uv);
  }

  void main() {
    // Hero keeps the swipe pan; the destination is always framed centered
    // (its aspect/headroom differ from the hero, so inheriting the pan would
    // push a portrait shot past its edge and smear it).
    vec3 c1 = sampleScene(uTex,  uDepth,  uFocus1, uZoom1, uImageAspect,  uPan).rgb;
    vec3 c2 = sampleScene(uTex2, uDepth2, uFocus2, uZoom2, uImageAspect2, vec2(0.0)).rgb;
    vec3 col = mix(c1, c2, clamp(uMix, 0.0, 1.0));

    // vignette
    vec2 dd = vUv - 0.5;
    float vig = smoothstep(0.95, 0.35, length(dd) * 1.25);
    col *= mix(0.82, 1.0, vig);

    // faint animated grain
    float g = hash(vUv * vec2(1920.0, 1080.0) + uTime) - 0.5;
    col += g * uGrain;

    gl_FragColor = vec4(col, 1.0);
  }
`;

const pendingInit = new WeakSet();

// Let the static hero <img> (the LCP element) finish before pulling in the
// three.js bundle, so the enhancement never competes with first paint.
// Times out rather than stalling the 3D forever on a hung image request.
function whenHeroImageSettled(root) {
  return new Promise(function (resolve) {
    const img = root.querySelector('.bohdy__photo img');
    if (!img || img.complete) { resolve(); return; }
    let settled = false;
    const done = function () {
      if (settled) return;
      settled = true;
      resolve();
    };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
    setTimeout(done, 4000);
  });
}

function init(root) {
  if (!root) return;
  if (root.getAttribute('data-motion-enabled') === 'false') return;
  if (prefersReducedMotion()) return;
  if (!webglAvailable()) return;
  if (scenes.has(root) || pendingInit.has(root)) return;
  if (!root.getAttribute('data-hero-src')) return;

  pendingInit.add(root);
  whenHeroImageSettled(root)
    .then(function () { return loadLibs(root); })
    .then(function (THREE) {
      pendingInit.delete(root);
      // The section may have been unloaded (theme editor) or the visitor may
      // have toggled reduced motion while the libraries were downloading.
      if (!root.isConnected || scenes.has(root) || prefersReducedMotion()) return;
      initScene(root, THREE);
    })
    .catch(function () {
      pendingInit.delete(root); // static <img> fallback stays in place
    });
}

function initScene(root, THREE) {
  // Prefer the static <img>'s resolved source: it is already in the HTTP cache
  // (the markup sets crossorigin, so the CORS-mode texture fetch can reuse it)
  // and it is the responsive variant sized for this viewport — no second
  // multi-hundred-KB hero download just for the texture.
  const heroImg = root.querySelector('.bohdy__photo img');
  const src = (heroImg && heroImg.currentSrc) || root.getAttribute('data-hero-src');

  const stage = root.querySelector('.bohdy__stage');
  if (!stage) return;

  const i = getIntensity(root);

  const canvas = document.createElement('canvas');
  canvas.setAttribute('aria-hidden', 'true');
  stage.appendChild(canvas);

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false });
  } catch (e) {
    canvas.remove();
    return;
  }
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

  const scene = new THREE.Scene();
  const camera = new THREE.Camera();

  // owned textures to dispose on teardown
  const owned = [];

  // mid-gray (0.5) fallback depth -> zero parallax when a map is missing.
  const grayTex = (function () {
    const t = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1, THREE.RGBAFormat);
    t.needsUpdate = true;
    return t;
  })();
  owned.push(grayTex);

  const uniforms = {
    uTex:           { value: null },
    uDepth:         { value: grayTex },
    uTex2:          { value: null },
    uDepth2:        { value: grayTex },
    uMix:           { value: 0 },
    uFocus1:        { value: new THREE.Vector2(0.5, 0.5) },
    uZoom1:         { value: IDLE_ZOOM },
    uFocus2:        { value: new THREE.Vector2(0.5, 0.5) },
    uZoom2:         { value: 1.0 },
    uPan:           { value: new THREE.Vector2(0, 0) },
    uOffset:        { value: new THREE.Vector2(0, 0) },
    uDepthStrength: { value: i.depth },
    uImageAspect:   { value: 1.5 },
    uImageAspect2:  { value: 1.5 },
    uViewAspect:    { value: 1.5 },
    uTime:          { value: 0 },
    uGrain:         { value: 0.05 },
    uFit:           { value: 0 },
    uPaper:         { value: new THREE.Color(0x0e0e10) },
  };

  // Letterbox colour for contain mode — read the section's --paper.
  try {
    const paper = getComputedStyle(root).getPropertyValue('--paper').trim();
    if (paper) uniforms.uPaper.value = new THREE.Color(paper);
  } catch (e) { /* keep default */ }

  const mobileFit = (root.getAttribute('data-mobile-fit') || 'cover').toLowerCase();
  const coarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  let contain = false;
  let swipe = false; // touch swipe-to-pan active (cover mode on touch devices)
  let flown = false; // flying into / arrived at a hotspot (gsap owns the camera)
  let attracting = false; // idle auto-tour owns zoom/focus (never while flown)

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    depthTest: false,
    depthWrite: false,
  });

  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material);
  scene.add(mesh);

  const loader = new THREE.TextureLoader();
  loader.crossOrigin = 'anonymous';

  const texCache = {};
  function loadTex(url, srgb, cb) {
    if (!url) { cb(null); return; }
    if (texCache[url]) { cb(texCache[url]); return; }
    loader.load(url, function (t) {
      t.minFilter = THREE.LinearFilter;
      t.generateMipmaps = false;
      if (srgb && 'colorSpace' in t && THREE.SRGBColorSpace) t.colorSpace = THREE.SRGBColorSpace;
      texCache[url] = t;
      owned.push(t);
      cb(t);
    }, undefined, function () { cb(null); });
  }

  // Hero color texture.
  loadTex(src, true, function (tex) {
    if (!tex) { teardownScene(scenes.get(root)); scenes.delete(root); return; }
    uniforms.uTex.value = tex;
    uniforms.uTex2.value = tex; // crossfade target defaults to hero until dest loads
    const img = tex.image;
    if (img && img.width && img.height) {
      uniforms.uImageAspect.value = img.width / img.height;
      uniforms.uImageAspect2.value = uniforms.uImageAspect.value;
    }
    // Draw one real (textured) frame BEFORE revealing the canvas, so the
    // fade-in never shows the empty black framebuffer.
    resize();
    renderer.render(scene, camera);
    root.classList.add('bohdy--3d-ready');
    root.dataset.fly = 'on'; // tells the section to route pin taps through flyTo
    screensReady = true;
  });

  // Hero depth map (drives parallax).
  loadTex(root.getAttribute('data-hero-depth'), false, function (dt) {
    uniforms.uDepth.value = dt || grayTex;
    uniforms.uDepth2.value = uniforms.uDepth.value;
  });

  // --- pointer / tilt parallax (eased) ---
  const target = { x: 0, y: 0 };
  const current = { x: 0, y: 0 };

  function onPointer(e) {
    if (coarsePointer) return; // touch devices pan by swipe, not pointer
    const nx = (e.clientX / window.innerWidth) * 2 - 1;
    const ny = (e.clientY / window.innerHeight) * 2 - 1;
    target.x = nx;
    target.y = ny;
  }
  function onOrient(e) {
    if (e.gamma == null || e.beta == null) return;
    target.x = Math.max(-1, Math.min(1, e.gamma / 30));
    target.y = Math.max(-1, Math.min(1, (e.beta - 45) / 30));
  }
  window.addEventListener('pointermove', onPointer, { passive: true });
  window.addEventListener('deviceorientation', onOrient, { passive: true });

  // --- swipe-to-pan (touch, cover mode) ---
  const drag = { x: 0 };
  let dragging = false, lastTX = 0, lastTY = 0, axisLock = null;

  function maxPanX() {
    const viewA = uniforms.uViewAspect.value;
    const imgA = uniforms.uImageAspect.value;
    const scaleX = viewA < imgA ? viewA / imgA : 1;
    const z = uniforms.uZoom1.value || 1;
    return Math.max(0, 0.5 / scaleX - 0.5 / z - 0.01);
  }

  function onTouchStart(e) {
    if (!swipe || flown || !e.touches || e.touches.length !== 1) return;
    dragging = true;
    axisLock = null;
    lastTX = e.touches[0].clientX;
    lastTY = e.touches[0].clientY;
  }
  function onTouchMove(e) {
    if (!swipe || flown || !dragging || !e.touches || e.touches.length !== 1) return;
    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    const dx = x - lastTX;
    const dy = y - lastTY;
    if (axisLock === null && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      axisLock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (axisLock === 'x') {
      if (e.cancelable) e.preventDefault();
      const mx = maxPanX();
      drag.x = Math.max(-mx, Math.min(mx, drag.x - (dx / window.innerWidth) * (2 * mx)));
    }
    lastTX = x;
    lastTY = y;
  }
  function onTouchEnd() { dragging = false; axisLock = null; }
  stage.addEventListener('touchstart', onTouchStart, { passive: true });
  stage.addEventListener('touchmove', onTouchMove, { passive: false });
  stage.addEventListener('touchend', onTouchEnd, { passive: true });
  stage.addEventListener('touchcancel', onTouchEnd, { passive: true });

  function resize() {
    const w = stage.clientWidth;
    const h = stage.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    uniforms.uViewAspect.value = w / h;
    contain = mobileFit === 'contain' && window.innerWidth <= 640;
    uniforms.uFit.value = contain ? 1 : 0;
    swipe = !contain && coarsePointer && maxPanX() > 0.02;
    if (!swipe) drag.x = 0;
  }
  const ro = new ResizeObserver(resize);
  ro.observe(stage);
  resize();

  // --- hotspot pin projection ---
  // Pins are authored in PHOTO space (data-focus-x/y). On touch devices the
  // photo pans (swipe) and zooms, so we project each pin from photo space to
  // the current on-screen position every frame; pins panned out of view fade
  // out, so visitors swipe to "discover" them. Desktop keeps the static CSS
  // positions (no swipe there).
  const pins = Array.from(root.querySelectorAll('.bohdy__hotspot'));
  pins.forEach(function (p) {
    p._ix = parseFloat(p.getAttribute('data-focus-x'));
    p._iy = 1 - parseFloat(p.getAttribute('data-focus-y')); // CSS top -> shader v (bottom-up)
  });

  function projectPins() {
    if (!coarsePointer || !pins.length) return;
    const z = uniforms.uZoom1.value || 1;
    const fx = uniforms.uFocus1.value.x;
    const fy = uniforms.uFocus1.value.y;
    const panx = uniforms.uPan.value.x;
    const pany = uniforms.uPan.value.y;
    const fit = uniforms.uFit.value;
    const imgA = uniforms.uImageAspect.value;
    const viewA = uniforms.uViewAspect.value;
    let sx, sy;
    if (fit < 0.5) { // cover
      if (viewA > imgA) { sx = 1; sy = imgA / viewA; } else { sx = viewA / imgA; sy = 1; }
    } else {         // contain
      if (viewA > imgA) { sx = viewA / imgA; sy = 1; } else { sx = 1; sy = imgA / viewA; }
    }
    for (let k = 0; k < pins.length; k++) {
      const p = pins[k];
      if (isNaN(p._ix) || isNaN(p._iy)) continue;
      const vx = fx + ((0.5 + (p._ix - 0.5) / sx) - panx - fx) * z;
      const vy = fy + ((0.5 + (p._iy - 0.5) / sy) - pany - fy) * z;
      const left = vx;
      const top = 1 - vy;
      p.style.left = (left * 100) + '%';
      p.style.top = (top * 100) + '%';
      p.classList.toggle('is-hidden', left < 0.03 || left > 0.97 || top < 0.0 || top > 1.0);
    }
  }

  // --- fly into / out of a hotspot ---
  function loadDest(dest, ddepth, onReady, onFail) {
    loadTex(dest, true, function (t) {
      if (!t) { if (onFail) onFail(); return; }
      uniforms.uTex2.value = t;
      if (t.image && t.image.width && t.image.height) {
        uniforms.uImageAspect2.value = t.image.width / t.image.height;
      }
      loadTex(ddepth, false, function (dt) {
        uniforms.uDepth2.value = dt || grayTex;
        if (onReady) onReady();
      });
    });
  }

  // --- idle attract tour ---
  // After a stretch with no interaction (desktop only), drift the camera from
  // hotspot to hotspot and reveal each label, so visitors discover the pins.
  // Any activity cancels instantly. GSAP-only: without it we simply never tour.
  const attractEnabled =
    (root.getAttribute('data-attract') || 'off') === 'on' && !coarsePointer;
  const ATTRACT_IDLE_MS = 12000;
  const ATTRACT_ZOOM = 1.3;
  let idleTimer = null;
  let attractTl = null;
  let attractIdx = 0;

  function armIdle() {
    if (!attractEnabled) return;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(startAttract, ATTRACT_IDLE_MS);
  }

  function stopAttract() {
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    const wasAttracting = attracting;
    attracting = false;
    if (attractTl) { attractTl.kill(); attractTl = null; }
    pins.forEach(function (p) { p.classList.remove('is-attract'); });
    if (wasAttracting && window.gsap && !flown) {
      window.gsap.to(uniforms.uZoom1, { value: IDLE_ZOOM, duration: 0.9, ease: 'power2.out' });
      window.gsap.to(uniforms.uFocus1.value, { x: 0.5, y: 0.5, duration: 0.9, ease: 'power2.inOut' });
    }
    armIdle();
  }

  function attractStep() {
    if (!attracting) return;
    const g = window.gsap;
    const pin = pins[attractIdx % pins.length];
    attractIdx += 1;
    if (!g || !pin) { stopAttract(); return; }
    // Authored photo coords -> screen uv (desktop shows the photo unpanned).
    const fx = parseFloat(pin.getAttribute('data-focus-x'));
    const fy = 1 - parseFloat(pin.getAttribute('data-focus-y'));
    if (isNaN(fx) || isNaN(fy)) { stopAttract(); return; }
    attractTl = g.timeline({ onComplete: attractStep });
    attractTl.to(uniforms.uFocus1.value, { x: fx, y: fy, duration: 2.4, ease: 'sine.inOut' }, 0);
    attractTl.to(uniforms.uZoom1, { value: ATTRACT_ZOOM, duration: 2.4, ease: 'sine.inOut' }, 0);
    attractTl.call(function () { pin.classList.add('is-attract'); }, null, 1.9);
    attractTl.to({}, { duration: 2.6 }); // hold on the hotspot, label visible
    attractTl.call(function () { pin.classList.remove('is-attract'); });
    attractTl.to(uniforms.uZoom1, { value: IDLE_ZOOM + 0.04, duration: 1.4, ease: 'sine.inOut' });
  }

  function startAttract() {
    idleTimer = null;
    if (!attractEnabled || attracting || flown || contain || document.hidden) { armIdle(); return; }
    if (!window.gsap || !pins.length) return; // nothing to tour / no animator
    attracting = true;
    attractStep();
  }

  function onActivity() {
    if (attracting) stopAttract();
    else armIdle();
  }

  const activityEvents = ['pointerdown', 'pointermove', 'wheel', 'keydown', 'touchstart'];
  if (attractEnabled) {
    activityEvents.forEach(function (ev) {
      window.addEventListener(ev, onActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', onActivity);
    armIdle();
  }

  function flyTo(pin) {
    if (!pin || flown) return;
    if (attracting || idleTimer) stopAttract();
    const fx = parseFloat(pin.getAttribute('data-focus-x'));
    const fy = parseFloat(pin.getAttribute('data-focus-y'));
    const dest = pin.getAttribute('data-dest');
    const ddepth = pin.getAttribute('data-dest-depth');
    const panel = pin.getAttribute('data-panel');
    flown = true;
    flownDest = dest || '';
    root.classList.add('bohdy--flying'); // hide pins/CTA immediately during the dolly

    // Dolly toward where the pin actually sits on screen right now (accounts
    // for swipe-pan/zoom on mobile), falling back to the authored photo coords.
    let focusX = isFinite(fx) ? fx : 0.5;
    let focusY = 1.0 - (isFinite(fy) ? fy : 0.5); // shader uv is bottom-up; CSS y% is top-down
    try {
      const pr = pin.getBoundingClientRect();
      const sr = stage.getBoundingClientRect();
      if (sr.width && sr.height) {
        focusX = (pr.left + pr.width / 2 - sr.left) / sr.width;
        focusY = 1.0 - (pr.top + pr.height / 2 - sr.top) / sr.height;
      }
    } catch (e) { /* keep authored coords */ }

    const arrive = function () {
      root.dispatchEvent(new CustomEvent('bohdy:flyin', { detail: { panel: panel } }));
    };

    const g = window.gsap;
    if (!g) {
      uniforms.uFocus1.value.set(focusX, focusY);
      uniforms.uZoom1.value = FLY_ZOOM;
      loadDest(dest, ddepth,
        function () { uniforms.uMix.value = 1; arrive(); },
        function () { arrive(); });
      return;
    }

    g.to(uniforms.uFocus1.value, { x: focusX, y: focusY, duration: 1.1, ease: 'power2.inOut' });
    g.to(uniforms.uZoom1, { value: FLY_ZOOM, duration: 1.25, ease: 'power2.in' });
    loadDest(dest, ddepth,
      function () {
        g.to(uniforms.uMix, { value: 1, duration: 0.7, ease: 'power1.inOut', delay: 0.5, onComplete: arrive });
        g.fromTo(uniforms.uZoom2, { value: 1.12 }, { value: 1.0, duration: 1.1, ease: 'power2.out', delay: 0.5 });
      },
      function () { g.delayedCall(1.0, arrive); });
  }

  function reset() {
    if (!flown) return;
    const done = function () {
      flown = false;
      flownDest = '';
      root.classList.remove('bohdy--flying');
    };
    const g = window.gsap;
    if (!g) {
      uniforms.uMix.value = 0;
      uniforms.uZoom1.value = IDLE_ZOOM;
      uniforms.uFocus1.value.set(0.5, 0.5);
      uniforms.uZoom2.value = 1.0;
      done();
      return;
    }
    g.to(uniforms.uMix, { value: 0, duration: 0.5, ease: 'power1.inOut' });
    g.to(uniforms.uZoom1, { value: IDLE_ZOOM, duration: 0.85, ease: 'power2.out' });
    g.to(uniforms.uFocus1.value, { x: 0.5, y: 0.5, duration: 0.85, ease: 'power2.inOut' });
    g.to(uniforms.uZoom2, { value: 1.0, duration: 0.6, onComplete: done });
  }

  const onFlyReq = function (e) { if (e.detail && e.detail.pin) flyTo(e.detail.pin); };
  const onFlyOut = function () { reset(); };
  root.addEventListener('bohdy:flyrequest', onFlyReq);
  root.addEventListener('bohdy:flyout', onFlyOut);

  // --- glass CTA float + tilt ---
  const cta = root.querySelector('.bohdy__cta');
  let bobTween = null;
  if (cta && window.gsap) {
    bobTween = window.gsap.to(cta, { y: -8, duration: 3, ease: 'sine.inOut', repeat: -1, yoyo: true });
  }

  const clock = new THREE.Clock();
  let raf = null;

  // Live menu boards (bohdy-screens.js): feed it the camera uniforms every
  // frame so the iframes stay glued to the TV panels. Only once the canvas is
  // actually visible — before that the static <img> (zoom 1) is showing.
  let screensReady = false;
  let flownDest = ''; // destination photo url while flying into a hotspot
  const screensState = {};
  function pushScreens() {
    const hook = root._bohdyScreens;
    if (!hook || !screensReady) return;
    screensState.z = uniforms.uZoom1.value;
    screensState.fx = uniforms.uFocus1.value.x;
    screensState.fy = uniforms.uFocus1.value.y;
    screensState.px = uniforms.uPan.value.x;
    screensState.py = uniforms.uPan.value.y;
    screensState.ox = uniforms.uOffset.value.x;
    screensState.oy = uniforms.uOffset.value.y;
    screensState.ds = uniforms.uDepthStrength.value;
    screensState.imgA = uniforms.uImageAspect.value;
    screensState.viewA = uniforms.uViewAspect.value;
    screensState.fit = uniforms.uFit.value;
    screensState.mix = uniforms.uMix.value;
    // Destination scene (hotspot fly-in): its own camera + which photo it is,
    // so the boards can re-glue to the menu wall inside scene-menu.jpg.
    screensState.z2 = uniforms.uZoom2.value;
    screensState.fx2 = uniforms.uFocus2.value.x;
    screensState.fy2 = uniforms.uFocus2.value.y;
    screensState.imgA2 = uniforms.uImageAspect2.value;
    screensState.dest = flownDest;
    hook(screensState);
  }

  function tick() {
    const t = clock.getElapsedTime();
    uniforms.uTime.value = t;

    current.x += (target.x - current.x) * 0.05;
    current.y += (target.y - current.y) * 0.05;

    if (flown) {
      // gsap owns zoom/focus/mix; freeze the swipe pan (no snap) and keep only
      // a gentle drift alive on the scene.
      uniforms.uOffset.value.set(
        Math.sin(t * 0.10) * i.drift * 0.6 + current.x * i.pan * 0.4,
        Math.cos(t * 0.08) * i.drift * 0.6 - current.y * i.pan * 0.4
      );
    } else if (attracting) {
      // The attract timeline owns zoom/focus; keep a soft drift, no pointer pan.
      uniforms.uMix.value = 0;
      uniforms.uPan.value.set(0, 0);
      uniforms.uOffset.value.set(
        Math.sin(t * 0.10) * i.drift * 0.5,
        Math.cos(t * 0.08) * i.drift * 0.5
      );
    } else if (contain) {
      uniforms.uZoom1.value = 1.0;
      uniforms.uFocus1.value.set(0.5, 0.5);
      uniforms.uPan.value.set(0, 0);
      uniforms.uOffset.value.set(0, 0);
      uniforms.uMix.value = 0;
    } else {
      uniforms.uZoom1.value = IDLE_ZOOM + Math.sin(t * 0.18) * 0.012;
      uniforms.uFocus1.value.set(0.5, 0.5);
      uniforms.uMix.value = 0;
      const driftX = Math.sin(t * 0.12) * i.drift;
      const driftY = Math.cos(t * 0.09) * i.drift;
      uniforms.uOffset.value.set(driftX + current.x * i.pan, driftY - current.y * i.pan);
      if (swipe) {
        const mx = maxPanX();
        uniforms.uPan.value.set(Math.max(-mx, Math.min(mx, drag.x)), 0);
      } else {
        uniforms.uPan.value.set(0, 0);
      }
    }

    if (cta && !window.gsap) {
      cta.style.transform =
        'perspective(700px) rotateX(' + (-current.y * i.tilt * 0.4) + 'deg) rotateY(' +
        (current.x * i.tilt * 0.4) + 'deg)';
    }

    if (!flown) projectPins();
    pushScreens();

    renderer.render(scene, camera);
    raf = requestAnimationFrame(tick);
  }
  raf = requestAnimationFrame(tick);

  const state = {
    renderer, material, mesh, ro, stage, canvas, root, owned, uniforms, pins,
    onPointer, onOrient, onTouchStart, onTouchMove, onTouchEnd, onFlyReq, onFlyOut, bobTween,
    cancel: function () { if (raf) cancelAnimationFrame(raf); raf = null; },
    cancelAttract: function () {
      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
      if (attractTl) { attractTl.kill(); attractTl = null; }
      attracting = false;
      if (attractEnabled) {
        activityEvents.forEach(function (ev) {
          window.removeEventListener(ev, onActivity);
        });
        document.removeEventListener('visibilitychange', onActivity);
      }
    },
  };
  scenes.set(root, state);
}

function teardownScene(state) {
  if (!state) return;
  state.cancel();
  if (state.cancelAttract) state.cancelAttract();
  // Hand the live menu boards back to their static (no-3D) projection.
  if (state.root && typeof state.root._bohdyScreens === 'function') {
    try { state.root._bohdyScreens(null); } catch (e) { /* screens module gone */ }
  }
  window.removeEventListener('pointermove', state.onPointer);
  window.removeEventListener('deviceorientation', state.onOrient);
  if (state.root) {
    state.root.removeEventListener('bohdy:flyrequest', state.onFlyReq);
    state.root.removeEventListener('bohdy:flyout', state.onFlyOut);
  }
  if (state.stage) {
    state.stage.removeEventListener('touchstart', state.onTouchStart);
    state.stage.removeEventListener('touchmove', state.onTouchMove);
    state.stage.removeEventListener('touchend', state.onTouchEnd);
    state.stage.removeEventListener('touchcancel', state.onTouchEnd);
  }
  if (state.ro) state.ro.disconnect();
  if (state.bobTween) state.bobTween.kill();
  if (window.gsap && state.uniforms) {
    const u = state.uniforms;
    window.gsap.killTweensOf([u.uMix, u.uZoom1, u.uZoom2, u.uFocus1.value]);
  }
  if (state.owned) state.owned.forEach(function (t) { if (t && t.dispose) t.dispose(); });
  if (state.material) state.material.dispose();
  if (state.mesh && state.mesh.geometry) state.mesh.geometry.dispose();
  if (state.renderer) state.renderer.dispose();
  if (state.canvas) state.canvas.remove();
  if (state.pins) state.pins.forEach(function (p) {
    p.style.left = '';
    p.style.top = '';
    p.classList.remove('is-hidden');
  });
  if (state.root && state.root.classList) {
    state.root.classList.remove('bohdy--3d-ready');
    state.root.classList.remove('bohdy--flying');
  }
  if (state.root && state.root.dataset) delete state.root.dataset.fly;
}

function destroy(root) {
  const state = scenes.get(root);
  if (state) {
    teardownScene(state);
    scenes.delete(root);
  }
}

function initAll() {
  document.querySelectorAll(SECTION_SELECTOR).forEach(init);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll, { once: true });
} else {
  initAll();
}

document.addEventListener('shopify:section:load', function (e) {
  if (!e || !e.target) return;
  const root = e.target.querySelector(SECTION_SELECTOR);
  if (root) init(root);
});

document.addEventListener('shopify:section:unload', function (e) {
  if (!e || !e.target) return;
  const root = e.target.querySelector(SECTION_SELECTOR);
  if (root) destroy(root);
});

if (window.matchMedia) {
  const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onChange = function () {
    if (rm.matches) {
      document.querySelectorAll(SECTION_SELECTOR).forEach(destroy);
    } else {
      initAll();
    }
  };
  if (typeof rm.addEventListener === 'function') rm.addEventListener('change', onChange);
  else if (typeof rm.addListener === 'function') rm.addListener(onChange);
}
